// src/web/routes/no-access.tsx

import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'

export const Route = createFileRoute('/no-access')({
	component: NoAccessPage,
})

function NoAccessPage() {
	const navigate = useNavigate()
	const [isChecking, setIsChecking] = useState(true)

	// Check if user actually has no access - redirect if they do have access
	useEffect(() => {
		const checkAccess = async () => {
			try {
				const { data, error, status } = await api.api.auth.me.get()
				if (!error && data?.user) {
					// User has valid access, redirect to home
					navigate({ to: '/home', replace: true })
					return
				}
				if (status === 401) {
					// Not authenticated, redirect to login
					navigate({ to: '/login', replace: true })
					return
				}
				// 403 or other error - user belongs on this page
				setIsChecking(false)
			} catch {
				// Error checking - stay on page
				setIsChecking(false)
			}
		}
		checkAccess()
	}, [navigate])

	if (isChecking) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="flex flex-col items-center gap-3">
					<div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin" />
					<span className="text-sm text-muted-foreground">Checking access...</span>
				</div>
			</div>
		)
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-background p-6">
			<div className="max-w-md w-full text-center">
				{/* Icon */}
				<div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-6">
					<ExclamationTriangleIcon className="w-8 h-8 text-amber-600" />
				</div>

				{/* Title */}
				<h1 className="text-2xl font-semibold text-foreground mb-2">No Access</h1>

				{/* Description */}
				<p className="text-muted-foreground mb-6">
					Your account does not have any access to this app. Please contact your workspace
					administrator.
				</p>

				{/* Buttons */}
				<div className="flex flex-col gap-3">
					<Button asChild>
						<a href="https://frndos.com">Go to frndos.com</a>
					</Button>
				</div>

				{/* Help */}
				<p className="text-sm text-muted-foreground mt-6">
					Need help? Contact support at{' '}
					<a href="mailto:hi@frndos.com" className="text-primary hover:text-primary/80 font-medium">
						hi@frndos.com
					</a>
				</p>
			</div>
		</div>
	)
}
